import useSquareService from "@app-contexts/dashboard/features/box-office/square/hooks/useSquareService";
import { VenueBasedInvoiceSerializer } from "@app-contexts/dashboard/shared/types/invoice";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import { useBasket } from "@shared/modules/basket/services/useBasket";
import { TicketBasket } from "@shared/modules/basket/types";
import {
  getBoxOfficePurchasePlatform,
  getSquareTerminalPurchasePlatform,
  isSquareTerminalPurchasePlatform,
} from "@shared/modules/basket/utils/basket-utils";
import { retrieveBasketWithRetry } from "@shared/modules/basket/utils/basket-retrieval-utils";
import { useUserVenueSession } from "@shared/modules/venue/hooks/useUserVenueSession";
import { useStore } from "@shared/store/useStore";
import { useCallback, useRef } from "react";
import { PurchaseSourcePlatformMapIdType } from "@shared/constants/enums/enums-types";
import { convertToItemGroups as defaultConvertToItemGroups } from "../../../../../../shared/modules/basket/utils/helpers";
import { useBoxOfficeConfig } from "../../data/services/box-office-config/useBoxOfficeConfig";
import { useGroupSale } from "../../group-sales/data/services/useGroupSale";
import VenueBasedTicketBasketRepository from "../data/repositories/VenueBasedTicketBasketRepository";
import { IVenueBasket } from "../interfaces/IVenueBasket";

/**
 * Service for venue based baskets. Should only be used directly when the component is only used
 * in the "dashboard" app-context. If using this service in a shared component, should use the `useBasketService` instead.
 * That hooks into the basket service context and allows the component to be agnostic of the basket service being used.
 */
export function useVenueBasket(
  config: TicketBasket = {
    purchase_source_platform: getBoxOfficePurchasePlatform().id,
    payment_type: PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
  }
): IVenueBasket {
  const { data: currentVenue } = useUserVenueSession();
  const { boxOfficeConfig, setConfigValue } = useBoxOfficeConfig();
  const venueId = currentVenue?.id;
  const paymentTypeConfig = boxOfficeConfig.payment_type ?? config.payment_type;

  const convertToItemGroups = useCallback(
    (
      items: Array<{
        item: unknown;
        quantity: number;
        parent?: number | null;
      }>
    ) => {
      let itemGroups = defaultConvertToItemGroups(
        Array.isArray(items) ? items : [items]
      );

      itemGroups = itemGroups.map((itemGroup) => {
        return {
          ...itemGroup,
          auto_checkin:
            itemGroup.auto_checkin ?? Boolean(boxOfficeConfig?.auto_checkin),
        };
      });
      return itemGroups;
    },
    [boxOfficeConfig, config]
  );

  const basketService = useBasket(
    DataProvider.Venue,
    {
      venueId,
      ...config,
      payment_type: paymentTypeConfig,
      other_payment_type: boxOfficeConfig.other_custom_type,
    },
    { convertToItemGroups }
  );

  const [setBasket] = useStore((state) => [state.setBasket]);

  const { useGetPairedDeviceCode } = useSquareService(venueId);
  const { data: squareAPI, isLoading } = useGetPairedDeviceCode();
  const isSquareTerminalPaired = Boolean(
    squareAPI?.device_status ===
      enums.SQUARE_DEVICE_PAIRING_STATUS_MAP.PAIRED.id
  );

  const { setGroupSale, overrideSubtotal } = useGroupSale(
    basketService.itemGroups,
    basketService.modifyBasket
  );

  /**
   * Get the purchase platform of the basket - uses explicit value from basket or defaults to box office platform
   */
  const purchaseSourcePlatform =
    basketService.purchaseSourcePlatform ?? getBoxOfficePurchasePlatform().id;

  /**
   * Ref to track the current payment type to avoid stale closures in callbacks.
   * This ensures updateBasketForSquare always reads the latest payment type.
   */
  const paymentTypeRef = useRef(basketService.paymentType);
  paymentTypeRef.current = basketService.paymentType;

  /**
   * Venue specific
   * Updates the included_in_stats field of the basket
   */
  const updateIncludedInStats = (value: boolean) => {
    return basketService.modifyBasket(
      { included_in_stats: value },
      { shouldPersist: false }
    );
  };

  /**
   * Venue specific
   */
  const updateMessageToCustomer = (message: string) => {
    return basketService.modifyBasket(
      { message_to_customer: message },
      { shouldPersist: false }
    );
  };

  /**
   * Venue specific
   */
  const updatePurchaseNote = (note: string) => {
    return basketService.modifyBasket(
      { purchase_note: note },
      { shouldPersist: false }
    );
  };
  /**
   * Update the subscribe_to_venue field of the basket.
   */
  const updateSubscribeToVenue = (subscribe: boolean) => {
    return basketService.modifyBasket(
      { subscribe_to_venue: subscribe },
      { shouldPersist: false }
    );
  };

  /**
   * Update box office basket with Square terminal configs.
   *
   * Sets the `is_square_terminal_purchase` field based on shouldUseSquare AND paymentType.
   * Only sets Square Terminal platform when payment type is CREDIT.
   * Also updates the square_terminal_checkout_id field to always be nullish,
   * as well as the purchase_source_platform.
   *
   * Uses paymentTypeRef to avoid stale closure issues - always reads the current payment type.
   */
  const updateBasketForSquare = useCallback(
    async (
      shouldUseSquare = true,
      opts = {
        shouldPersist: true,
      }
    ) => {
      // Read from ref to get the latest payment type (avoids stale closure)
      const currentPaymentType = paymentTypeRef.current;

      const basketUpdates: Partial<TicketBasket> =
        shouldUseSquare &&
        currentPaymentType === PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id
          ? {
              is_square_terminal_purchase: true,
              extended_expiry_enabled: true,
              square_terminal_checkout_id: null,
              purchase_source_platform: getSquareTerminalPurchasePlatform().id,
            }
          : {
              is_square_terminal_purchase: false,
              extended_expiry_enabled: false,
              square_terminal_checkout_id: null,
              purchase_source_platform: getBoxOfficePurchasePlatform().id,
            };

      return basketService.modifyBasket(basketUpdates, opts).then((result) => {
        setConfigValue({
          ...boxOfficeConfig,
          prefers_square_terminal: shouldUseSquare,
        });
        return result;
      });
    },
    [basketService.modifyBasket, boxOfficeConfig, setConfigValue]
  );

  /**
   * Overload useBasket.updatePaymentType.
   * This function updates payment_type and sets purchase_source_platform based on payment type:
   * - CREDIT with Square Terminal paired → Square Terminal platform
   * - All other payment types → Box Office platform
   */
  const updatePaymentType = useCallback(
    (
      paymentType: number,
      otherPaymentType?: number,
      overridePurchaseSourcePlatform?: PurchaseSourcePlatformMapIdType
    ) => {
      // Determine platform based on payment type:
      // - CREDIT with Square Terminal → Square Terminal platform
      // - Everything else → Box Office platform
      const shouldUseSquareTerminal =
        paymentType === PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id &&
        isSquareTerminalPaired;

      const newPurchaseSourcePlatform =
        overridePurchaseSourcePlatform ??
        (shouldUseSquareTerminal
          ? getSquareTerminalPurchasePlatform().id
          : getBoxOfficePurchasePlatform().id);

      // save the payment type to local storage if its not complimentary or free
      if (
        !(
          [
            PAYMENT_TYPE_OPTIONS_MAP.COMPLIMENTARY.id,
            PAYMENT_TYPE_OPTIONS_MAP.FREE.id,
            PAYMENT_TYPE_OPTIONS_MAP.USER_CREDIT.id,
            PAYMENT_TYPE_OPTIONS_MAP.EXCHANGE_CREDIT.id,
          ] as Array<number>
        ).includes(paymentType)
      ) {
        setConfigValue({
          ...boxOfficeConfig,
          payment_type: paymentType,
          other_custom_type: otherPaymentType,
        });
      }

      return basketService.modifyBasket(
        {
          payment_type: paymentType,
          other_payment_type: otherPaymentType || null,
          purchase_source_platform: newPurchaseSourcePlatform,
          is_square_terminal_purchase: isSquareTerminalPurchasePlatform(
            newPurchaseSourcePlatform
          ),
        },
        { shouldPersist: true }
      );
    },
    [basketService.modifyBasket, isSquareTerminalPaired]
  );

  /**
   * This function updates the oher payment type on the basket.
   */
  const updateOtherPaymentType = useCallback(
    (otherPaymentType: number) => {
      setConfigValue({
        ...boxOfficeConfig,
        other_custom_type: otherPaymentType,
      });
      return basketService.modifyBasket(
        { other_payment_type: otherPaymentType },
        { shouldPersist: true }
      );
    },
    [basketService.modifyBasket]
  );

  /**
   * Overload useBasket.updatePurchasePlatform to take into account purchase_source_platform as
   * determined by this useVenueBasket hook
   */
  const updatePurchasePlatform = useCallback(() => {
    return basketService.modifyBasket(
      {
        purchase_source_platform: purchaseSourcePlatform,
      },
      {
        shouldPersist: true,
      }
    );
  }, [basketService.modifyBasket, purchaseSourcePlatform]);

  const updateIsSquarePosPurchase = useCallback(
    (value: boolean) => {
      return basketService.modifyBasket(
        {
          is_square_qpos_purchase: value,
        },
        { shouldPersist: false }
      );
    },
    [basketService.modifyBasket]
  );

  const applyExchangeCredits = useCallback(
    (amount: number, exchangeFromInvoice: VenueBasedInvoiceSerializer) => {
      const isAutoGeneratedPayment =
        exchangeFromInvoice.payment_type ===
        PAYMENT_TYPE_OPTIONS_MAP.AUTO_GENERATED.id;

      // Don't update payment type if it's auto-generated
      const paymentTypeToUse = isAutoGeneratedPayment
        ? basketService.paymentType
        : exchangeFromInvoice.payment_type;

      if (!basketService.hasCreditsApplied) {
        if (exchangeFromInvoice.user) {
          basketService
            .modifyBasket(
              {
                user: exchangeFromInvoice.user.id,
                email: exchangeFromInvoice.user.email,
                payment_type: paymentTypeToUse,
                credit_exchange_invoice_transaction_id:
                  exchangeFromInvoice.transaction_id,
              },
              { shouldPersist: false }
            )
            .then(() =>
              basketService.applyCredits(
                enums.SHOWPASS_CREDIT_TYPES.EXCHANGE_CREDIT.id,
                amount
              )
            );
        } else if (exchangeFromInvoice?.venue_user) {
          basketService
            .modifyBasket(
              {
                venue_user: exchangeFromInvoice.venue_user.id,
                payment_type: paymentTypeToUse,
                credit_exchange_invoice_transaction_id:
                  exchangeFromInvoice.transaction_id,
              },
              { shouldPersist: false }
            )
            .then(() =>
              basketService.applyCredits(
                enums.SHOWPASS_CREDIT_TYPES.EXCHANGE_CREDIT.id,
                amount
              )
            );
        } else {
          basketService.applyCredits(
            enums.SHOWPASS_CREDIT_TYPES.EXCHANGE_CREDIT.id,
            amount
          );
        }
      }
    },
    [basketService.applyCredits, basketService.modifyBasket]
  );

  /**
   * Refreshes the basket by fetching the latest data from the server
   * and updating the local store like zustand
   */
  const refresh = useCallback(
    // Set no_lock=true to avoid locking the basket when only reading data
    // This is useful when you need to fetch the latest basket state but don't plan to modify it
    async ({ no_lock = false } = {}) => {
      const basketRepo = new VenueBasedTicketBasketRepository({
        venueId,
      });

      return retrieveBasketWithRetry(async () => {
        const basket = await basketRepo.retrieve(basketService.basketId, {
          params: {
            ...(no_lock ? { no_lock } : {}),
          },
          // Suppress toast on error since we handle lock timeout errors with retry
          showToastOnError: false,
        });
        setBasket(DataProvider.Venue, basket);
        return basket;
      });
    },
    [venueId, basketService.basketId, setBasket]
  );

  /**
   * Fetches the basket from the server without updating the local store
   */
  const fetch = useCallback(async () => {
    const basketRepo = new VenueBasedTicketBasketRepository({
      venueId,
    });

    return basketRepo.retrieve(basketService.basketId).then((basket) => {
      return basket;
    });
  }, [venueId, basketService.basketId]);

  return {
    // useBasket
    addItems: basketService.addItems,
    modifyBasket: basketService.modifyBasket,
    modifyItems: basketService.modifyItems,
    modifyItemGroup: basketService.modifyItemGroup,
    modifyItemGroups: basketService.modifyItemGroups,
    removeItems: basketService.removeItems,
    updateCustomer: basketService.updateCustomer,
    expireBasket: basketService.expireBasket,
    extendBasket: basketService.extendBasket,
    getSubItemGroups: basketService.getSubItemGroups,
    itemsRequiresSeatSelection: basketService.itemsRequiresSeatSelection,
    applyDiscount: basketService.applyDiscount,
    applyBulkDiscounts: basketService.applyBulkDiscounts,
    removeDiscounts: basketService.removeDiscounts,
    save: basketService.save,
    applyCredits: basketService.applyCredits,
    removeCredits: basketService.removeCredits,
    enableExtendedExpiry: basketService.enableExtendedExpiry,
    setStatus: basketService.setStatus,
    updateExpiryDate: basketService.updateExpiryDate,
    emptyBasket: basketService.emptyBasket,
    updateBillingAddress: basketService.updateBillingAddress,
    updateShippingAddress: basketService.updateShippingAddress,
    updatePurchaserInfo: basketService.updatePurchaserInfo,
    updateIsStripeWalletPayment: basketService.updateIsStripeWalletPayment,
    updateAcceptedTerms: basketService.updateAcceptedTerms,
    updateGuestQuestions: basketService.updateGuestQuestions,
    updatePaymentMethod: basketService.updatePaymentMethod,
    paymentIntentOutcomeMessage: basketService.paymentIntentOutcomeMessage,
    basketEventIds: basketService.basketEventIds,
    paymentIntentStatus: basketService.paymentIntentStatus,
    currency: basketService.currency,
    email: basketService.email,
    totalPrice: basketService.totalPrice,
    creditApplied: basketService.creditApplied,
    appliedDiscounts: basketService.appliedDiscounts,
    hasEntireOrderDiscount: basketService.hasEntireOrderDiscount,
    paymentVenue: basketService.paymentVenue,
    invoiceId: basketService.invoiceId,
    isSquareTerminalPurchase: basketService.isSquareTerminalPurchase,
    paymentGateway: basketService.paymentGateway,
    soldByVenue: basketService.soldByVenue,
    itemGroups: basketService.itemGroups,
    hasItems: basketService.hasItems,
    totalItemQuantity: basketService.totalItemQuantity,
    isBasketUpdating: basketService.isBasketUpdating,
    isPurchasing: basketService.isPurchasing,
    setIsPurchasing: basketService.setIsPurchasing,
    includedInStats: basketService.includedInStats,
    paymentType: basketService.paymentType,
    basketId: basketService.basketId,
    basketUuid: basketService.basketUuid,
    basketUuid2: basketService.basketUuid2,
    expiryDate: basketService.expiryDate,
    purchaseNote: basketService.purchaseNote,
    messageToCustomer: basketService.messageToCustomer,
    squareTerminalCheckoutId: basketService.squareTerminalCheckoutId,
    squareLocationId: basketService.squareLocationId,
    status: basketService.status,
    nameOnTicket: basketService.nameOnTicket,
    otherPaymentType: basketService.otherPaymentType,
    enforceBoxOfficeInfoCollection:
      basketService.enforceBoxOfficeInfoCollection,
    collectEnhancedInfo: basketService.collectEnhancedInfo,
    hasAdditionalInfoCollection: basketService.hasAdditionalInfoCollection,
    hasShippingAddressCollection: basketService.hasShippingAddressCollection,
    enhancedInfoQuestions: basketService.enhancedInfoQuestions,
    enhancedInfoAddress: basketService.enhancedInfoAddress,
    hasCreditsApplied: basketService.hasCreditsApplied,
    creditType: basketService.creditType,
    basketItemTypes: basketService.basketItemTypes,
    isGroupSale: basketService.isGroupSale,
    areAllRenewalItemGroups: basketService.areAllRenewalItemGroups,
    eachItemGroupHasSeatOrLocationPermission:
      basketService.eachItemGroupHasSeatOrLocationPermission,
    itemGroupsWithNoSeatOrLocationPermission:
      basketService.itemGroupsWithNoSeatOrLocationPermission,
    isFree: basketService.isFree,
    isExpired: basketService.isExpired,
    userId: basketService.userId,
    customInfoQuestions: basketService.customInfoQuestions,
    discountMessageObject: basketService.discountMessageObject,
    selectedCustomSubTicketOptions:
      basketService.selectedCustomSubTicketOptions,
    shippingAddress: basketService.shippingAddress,
    billingAddress: basketService.billingAddress,
    shippingCost: basketService.shippingCost,
    phoneNumber: basketService.phoneNumber,
    paymentMethod: basketService.paymentMethod,
    subscribeToVenue: basketService.subscribeToVenue,
    hasTickets: basketService.hasTickets,
    hasProducts: basketService.hasProducts,
    hasMemberships: basketService.hasMemberships,
    hasGiftCards: basketService.hasGiftCards,
    refundProtectionToken: basketService.refundProtectionToken,
    supportsRefundProtection: basketService.supportsRefundProtection,
    holdSlug: basketService.holdSlug,
    // useVenueBasket
    updateIncludedInStats,
    updateMessageToCustomer,
    updateSubscribeToVenue,
    updatePurchaseNote,
    updatePaymentType,
    updateOtherPaymentType,
    updateBasketForSquare,
    refresh,
    updatePurchasePlatform,
    applyExchangeCredits,
    purchaseSourcePlatform,
    isLoading,
    isSquareTerminalPaired,
    setGroupSale,
    overrideSubtotal,
    updateIsSquarePosPurchase,
    fetch,
  };
}
